/**
 * Ring buffer of recent runtime errors + unhandled promise rejections
 * for inclusion in the diagnostics report (#1847).
 *
 * Installed once at app boot via installErrorBuffer(). The buffer lives in
 * module-local memory and resets on page reload. We don't persist - this
 * is for the "what just broke" debugging case, not long-term telemetry.
 */

export interface CapturedError {
  at: string; // ISO timestamp
  kind: 'error' | 'unhandledrejection' | 'console.error';
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  col?: number;
}

const MAX_ENTRIES = 50;
const buffer: CapturedError[] = [];

function push(entry: CapturedError) {
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) {
    buffer.splice(0, buffer.length - MAX_ENTRIES);
  }
}

function safeStringify(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

let installed = false;

export function installErrorBuffer(): void {
  if (installed) return;
  installed = true;

  // Global window.onerror catch.
  window.addEventListener('error', (event) => {
    push({
      at: new Date().toISOString(),
      kind: 'error',
      message: event.message || 'unknown error',
      stack: event.error?.stack,
      source: event.filename,
      line: event.lineno,
      col: event.colno,
    });
  });

  // Unhandled promise rejections.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    push({
      at: new Date().toISOString(),
      kind: 'unhandledrejection',
      message: safeStringify(reason instanceof Error ? reason.message : reason),
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });

  // Wrap console.error so explicit error logs are captured too. Keep the
  // original behavior - this is a passive capture, not a replacement.
  const origConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      push({
        at: new Date().toISOString(),
        kind: 'console.error',
        message: args.map(safeStringify).join(' '),
      });
    } catch {
      // Defensive: never let the buffer break console.error itself.
    }
    origConsoleError(...args);
  };
}

export function getRecentErrors(): CapturedError[] {
  return buffer.slice();
}

export function clearErrorBuffer(): void {
  buffer.length = 0;
}
